/**
 * Vello effect / microinteraction layer — the Claude Design "Modernização com efeitos" `tokens/effects.css`
 * (FX-01…16) plus the entrance recipes of the Admin Web A08 layers, ported into the shared design system (D547).
 *
 * Two families live here, and the difference matters for reduced motion:
 *
 *  1. **FX classes** (`vello-fx-*`) — the design's `.fx-*` effects: the card lift, the press, the skeleton shimmer, the
 *     live pulse, the progress sweep, the cascade, the row reveal, the FX-03 scrim/sheet, the toast, the accordion and the
 *     route line. Under `prefers-reduced-motion: reduce` these are stilled EXACTLY as the design's last block does
 *     (`transition: none` / `animation: none`, and the card hover stays put).
 *  2. **Layer classes** (`vello-layer-*` + `vello-in`) — the entrance of the A08 menu (`pop`), drawer (`panel`), confirm
 *     dialog (`dialog`), command palette (`palette`), photo modal (`photo`) and their scrim. In the design these are inline
 *     transitions, not `.fx-*` classes, so they keep playing under reduced motion (owner decision, D546 #5: hover, press,
 *     menus, drawers, dialogs, the sidebar width and the count-up keep animating).
 *
 * Everything is built from the motion tokens — no bounce, no spring. The raw values live here and in the sibling token
 * files (the visual-token gate exempts `@vello/tokens`); every consumer references the emitted classes/keyframes and
 * never a literal in app code. Idempotent to inject once at boot (`installVelloTheme()` does it).
 */
import { EASING, DURATION } from './motion.js';
/** Class names the effect layer defines, so consumers reference them symbolically rather than as string literals. */
export const EFFECT_CLASS = {
    // ---- FX classes (stilled under reduced motion, like the design) ----
    /** FX-04 skeleton: the `#EDEFF2 → #F7F8FA → #EDEFF2` shimmer, 200px, 1.2s linear. */
    skeleton: 'vello-fx-skeleton',
    /** FX-06 live-status dot: a 2s ring that grows off the dot. */
    liveDot: 'vello-fx-live',
    /** A short rise+fade for a placeholder / route appearing (the design's `fx-in`). */
    fadeIn: 'vello-fx-fade-in',
    /** A one-shot gloss across an element (a just-arrived card); the infinite bar form is {@link EFFECT_CLASS.progressSweep}. */
    sweep: 'vello-fx-sweep',
    /** FX-06 progress sweep: a light band running across a progress bar, 1.8s linear, looping. */
    progressSweep: 'vello-fx-progress-sweep',
    /** FX-01 live card: lifts 2px with a soft shadow and an ink edge on hover, presses to .98. (Also `card`.) */
    hoverLift: 'vello-fx-card',
    card: 'vello-fx-card',
    /** FX-02 press: scales to .97 while pressed. */
    press: 'vello-fx-press',
    /** FX-07 cascade: children rise in sequence on mount; toggle `staggerOut` for ~30ms then remove it to replay. */
    stagger: 'vello-fx-stagger',
    staggerOut: 'vello-fx-out',
    /** FX-09 list row: hover tint + the row's `rowActions` slide in (also on keyboard focus within the row). */
    row: 'vello-fx-row',
    rowActions: 'vello-fx-row-actions',
    /** FX-03 scrim + sheet: mount with the class, add `in` ~30ms later; remove `in` then unmount after the slow duration. */
    fxScrim: 'vello-fx-scrim',
    sheet: 'vello-fx-sheet',
    /** FX-08 toast with a 4s life bar (`life` is the bar element inside the toast). */
    toast: 'vello-fx-toast',
    life: 'vello-fx-life',
    /** FX-14 accordion: the panel grows to `--fx-acc-max` (default 240px) and fades in when `in` is on. */
    acc: 'vello-fx-acc',
    /** FX-16 route: an SVG path (`routeLine`) inside `route` draws itself (stroke-dashoffset, 900ms) when `in` is on. */
    route: 'vello-fx-route',
    routeLine: 'vello-fx-line',
    /** FX-10 glass: a frosted bar, ONLY over a map. */
    glass: 'vello-fx-glass',
    // ---- Layer classes (keep playing under reduced motion, like the design's inline layer transitions) ----
    /** The state class of every layer: mount with the layer class, add `in` ~30ms later; remove it, wait, unmount. */
    in: 'vello-in',
    /** The scrim of the A08 layers (drawer, dialog, palette, photo): opacity .2s. */
    scrim: 'vello-layer-scrim',
    /** A right-hand record panel (the web-shell drawer): slides 40px from the right (.32s) with a fade (.2s). */
    panel: 'vello-layer-panel',
    /** A header/column popover: 6px drop with a fade (.2s). */
    pop: 'vello-layer-pop',
    /** A focal dialog: scales from .96 (.24s) with a fade (centre it with a flex container, not a transform). */
    dialog: 'vello-layer-dialog',
    /** The ⌘K command palette: drops 12px (.24s) with a fade. */
    palette: 'vello-layer-palette',
    /** The photo modal: scales from .9 (.28s) with a fade. */
    photo: 'vello-layer-photo',
};
/** Whether a class name belongs to the FX family (the one reduced motion stills). `vello-fx-*` or the design's `fx-*`. */
export function isEffectClass(name) {
    return /^(?:vello-)?fx-/.test(name);
}
/** Selectors of the FX family that declare a TRANSITION — each is stilled (`transition: none`) under reduced motion. */
export const REDUCED_TRANSITION_SELECTORS = [
    `.${EFFECT_CLASS.card}`,
    `.${EFFECT_CLASS.press}`,
    `.${EFFECT_CLASS.stagger} > *`,
    `.${EFFECT_CLASS.fxScrim}`,
    `.${EFFECT_CLASS.sheet}`,
    `.${EFFECT_CLASS.toast}`,
    `.${EFFECT_CLASS.life}`,
    `.${EFFECT_CLASS.row}`,
    `.${EFFECT_CLASS.row} .${EFFECT_CLASS.rowActions}`,
    `.${EFFECT_CLASS.acc}`,
    `.${EFFECT_CLASS.route} .${EFFECT_CLASS.routeLine}`,
];
/** Selectors of the FX family that declare an ANIMATION — each is stopped (`animation: none`) under reduced motion. */
export const REDUCED_ANIMATION_SELECTORS = [
    `.${EFFECT_CLASS.skeleton}`,
    `.${EFFECT_CLASS.liveDot}`,
    `.${EFFECT_CLASS.fadeIn}`,
    `.${EFFECT_CLASS.sweep}::after`,
    `.${EFFECT_CLASS.progressSweep}::after`,
];
/**
 * The effect stylesheet as a string, ready to drop into a `<style>` element. All timings come from the motion tokens.
 * The reduced-motion block at the end is the design's: it stills the FX classes and nothing else.
 */
export function renderEffectsCss() {
    const std = EASING.standard;
    const fast = `${DURATION.fast}ms`;
    const base = `${DURATION.base}ms`;
    const slow = `${DURATION.slow}ms`;
    const c = EFFECT_CLASS;
    return `
@keyframes vello-fx-shimmer { 0% { background-position: -200px 0; } 100% { background-position: 200px 0; } }
@keyframes vello-fx-pulse { 0% { box-shadow: 0 0 0 0 var(--color-success-pulse); } 70% { box-shadow: 0 0 0 9px transparent; } 100% { box-shadow: 0 0 0 0 transparent; } }
@keyframes vello-fx-fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes vello-fx-sweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(240%); } }

/* FX-04 skeleton — base/peak stops, 200px band, 1.2s linear (loading, never a spinner-only signal). */
.${c.skeleton} {
  background: linear-gradient(90deg, var(--color-skeleton-base) 25%, var(--color-skeleton-peak) 50%, var(--color-skeleton-base) 75%);
  background-size: 200px 100%;
  animation: vello-fx-shimmer 1200ms linear infinite;
  border-radius: var(--radius-sm);
}

/* FX-06 live-status dot — a 2s ring that grows off the dot (paired with a text label, never colour-only). */
.${c.liveDot} { animation: vello-fx-pulse 2000ms ${std} infinite; }

/* Entrance — a short rise+fade for a placeholder/route appearing (the design's fx-in / fx-rise). */
/* 'backwards', not 'both': once the entrance ends nothing keeps animating transform, so the element does not stay a
   containing block for fixed descendants (a drawer and its scrim inside a faded route would be clipped to the content). */
.${c.fadeIn} { animation: vello-fx-fade-in ${slow} ${std} backwards; }

/* FX-06 sweep — a light band crossing the element: once (a just-arrived card) or looping (a progress bar), 1.8s linear. */
.${c.sweep}, .${c.progressSweep} { position: relative; overflow: hidden; }
.${c.sweep}::after, .${c.progressSweep}::after {
  content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 40%; pointer-events: none;
  background: linear-gradient(90deg, transparent, var(--color-on-inverse-life), transparent);
  transform: translateX(-100%);
}
.${c.sweep}::after { animation: vello-fx-sweep 1800ms linear 1; }
.${c.progressSweep}::after { animation: vello-fx-sweep 1800ms linear infinite; }

/* FX-01 live card — lifts 2px with a soft shadow and an ink edge; pressing sets it back down at .98. */
.${c.card} {
  transition: transform ${fast} ${std}, box-shadow ${fast} ${std}, border-color ${fast} ${std};
}
.${c.card}:hover { transform: translateY(-2px); box-shadow: var(--shadow-card-hover); border-color: var(--color-border-strong); }
.${c.card}:active { transform: translateY(0) scale(0.98); box-shadow: none; }

/* FX-02 press — a scale-down on active (buttons/tappable rows). */
.${c.press} { transition: transform ${fast} ${std}; }
.${c.press}:active { transform: scale(0.97); }

/* FX-07 cascade — children rise in sequence (60ms steps, the 6th is the last step); \`.${c.staggerOut}\` resets them
   instantly so removing it replays. */
.${c.stagger} > * { transition: opacity ${slow} ${std}, transform ${slow} ${std}; }
.${c.stagger} > *:nth-child(2) { transition-delay: 60ms; }
.${c.stagger} > *:nth-child(3) { transition-delay: 120ms; }
.${c.stagger} > *:nth-child(4) { transition-delay: 180ms; }
.${c.stagger} > *:nth-child(5) { transition-delay: 240ms; }
.${c.stagger} > *:nth-child(n+6) { transition-delay: 300ms; }
.${c.stagger}.${c.staggerOut} > * { opacity: 0; transform: translateY(10px); transition: none !important; }

/* FX-09 list row — hover tint; the row's actions slide in on hover and on keyboard focus within the row. */
.${c.row} { transition: background ${fast} ${std}; }
.${c.row}:hover { background: var(--color-surface-subtle); }
.${c.row} .${c.rowActions} { opacity: 0; transform: translateX(6px); transition: opacity ${fast} ${std}, transform ${fast} ${std}; }
.${c.row}:hover .${c.rowActions}, .${c.row}:focus-within .${c.rowActions} { opacity: 1; transform: none; }
/* Touch screens have no hover: the row actions stay visible, or a tablet could never reach them (D505 wave, D511 finding). */
@media (hover: none) { .${c.row} .${c.rowActions} { opacity: 1; transform: none; } }

/* FX-03 scrim + sheet — mount, then add \`.${c.in}\` ~30ms later; remove it, wait the slow duration, unmount. */
.${c.fxScrim} { opacity: 0; transition: opacity ${base} ${std}; }
.${c.fxScrim}.${c.in} { opacity: 1; }
.${c.sheet} { transform: translateY(105%); transition: transform ${slow} ${std}; }
.${c.sheet}.${c.in} { transform: none; }

/* FX-08 toast — rises in (14px, .98); its life bar drains over 4s while shown. */
.${c.toast} { opacity: 0; transform: translateY(14px) scale(0.98); transition: opacity ${base} ${std}, transform ${base} ${std}; }
.${c.toast}.${c.in} { opacity: 1; transform: none; }
.${c.life} { width: 100%; transition: width ${DURATION.toastLife}ms linear; }
.${c.toast}.${c.in} .${c.life} { width: 0; }

/* FX-14 accordion — max-height + opacity; the chevron beside it rotates with the same state. */
.${c.acc} { overflow: hidden; max-height: 0; opacity: 0; transition: max-height ${slow} ${std}, opacity ${base} ${std}; }
.${c.acc}.${c.in} { max-height: var(--fx-acc-max, 240px); opacity: 1; }

/* FX-16 route — the path draws itself. */
.${c.route} .${c.routeLine} { stroke-dasharray: 340; stroke-dashoffset: 340; transition: stroke-dashoffset 900ms ${std}; }
.${c.route}.${c.in} .${c.routeLine} { stroke-dashoffset: 0; }

/* FX-10 glass — a frosted bar. ONLY over a map (the design restricts it): never on a plain page. */
.${c.glass} { background: var(--color-surface-glass); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); }

/* ---- Layers of the A08 shell (menu, drawer, dialog, palette, photo): NOT stilled by reduced motion ----------------
   The design draws these with inline transitions, so they keep playing when the person asks for less motion; only the
   FX family above is stilled. Mount with the layer class, add \`.${c.in}\` ~30ms later; remove it, wait, unmount. */
.${c.scrim} { opacity: 0; transition: opacity ${base} ${std}; }
.${c.scrim}.${c.in} { opacity: 1; }
.${c.panel} { opacity: 0; transform: translateX(40px); transition: transform ${slow} ${std}, opacity ${base} ${std}; }
.${c.panel}.${c.in} { opacity: 1; transform: none; }
.${c.pop} { opacity: 0; transform: translateY(-6px); transition: opacity ${base} ${std}, transform ${base} ${std}; }
.${c.pop}.${c.in} { opacity: 1; transform: none; }
.${c.dialog} { opacity: 0; transform: scale(0.96); transition: transform ${DURATION.dialog}ms ${std}, opacity ${base} ${std}; }
.${c.dialog}.${c.in} { opacity: 1; transform: none; }
.${c.palette} { opacity: 0; transform: translateY(-12px); transition: transform ${DURATION.dialog}ms ${std}, opacity ${DURATION.dialog}ms ${std}; }
.${c.palette}.${c.in} { opacity: 1; transform: none; }
.${c.photo} { opacity: 0; transform: scale(0.9); transition: transform ${DURATION.photo}ms ${std}, opacity ${base} ${std}; }
.${c.photo}.${c.in} { opacity: 1; transform: none; }

/* Reduced motion — the design's block (tokens/effects.css) and nothing more (D547, owner decision of the design-fidelity
   wave). ONLY the FX classes are stilled: their transitions are removed, their loops stop, the card does not lift. Hover,
   press, menus, drawers, dialogs, the sidebar width and the count-up keep animating exactly as the design plays them. */
@media (prefers-reduced-motion: reduce) {
  ${REDUCED_TRANSITION_SELECTORS.join(',\n  ')} {
    transition: none !important;
  }
  ${REDUCED_ANIMATION_SELECTORS.join(',\n  ')} {
    animation: none !important;
  }
  .${c.card}:hover { transform: none; }
}
`.trim();
}
//# sourceMappingURL=effects.js.map